import  { type JSX } from 'react';
import { ConfirmacionDialog } from './ConfirmacionDialog-ui';
import { useInactivity } from '@rfacil/shared/hooks';
import { useAuthStore } from '@rfacil/shared/services';

/**
 * @interface InactivityDialogProps
 * @description Propiedades del componente InactivityDialog.
 * @property {number} [inactivityTime] - Tiempo en milisegundos antes de mostrar el diálogo (por defecto: 30 segundos).
 * @property {number} [dialogTimeout] - Tiempo en milisegundos antes de cerrar sesión automáticamente cuando el diálogo está abierto (por defecto: 1 minuto).
 * @property {boolean} [enabled] - Si está habilitado el detector de inactividad (por defecto: true).
 */
export interface InactivityDialogProps {
  inactivityTime?: number;
  dialogTimeout?: number;
  enabled?: boolean;
}

/**
 * @component InactivityDialog
 * @description Componente que detecta inactividad del usuario y muestra un diálogo de confirmación.
 * Si el usuario no interactúa con el sistema durante un tiempo determinado, se muestra un diálogo.
 * Si el usuario acepta o no responde en 1 minuto, se cierra la sesión automáticamente.
 * @param {InactivityDialogProps} props - Propiedades del componente.
 * @returns {JSX.Element} El componente renderizado con el diálogo de inactividad.
 */
export const InactivityDialog = ({
  inactivityTime = 10 * 60 * 1000, // 30 segundos por defecto
  dialogTimeout = 60 * 1000, // 1 minuto por defecto
  enabled = true,
}: InactivityDialogProps): JSX.Element => {
  const { logout, isAuthenticated } = useAuthStore();

  const { showDialog, handleConfirm, handleCancel } = useInactivity(
    inactivityTime,
    dialogTimeout,
    logout,
    enabled && isAuthenticated
  );

  if (!enabled || !isAuthenticated) {
    return <></>;
  }

  return (
    <ConfirmacionDialog
      open={showDialog}
      onClose={handleCancel}
      onConfirm={handleConfirm}
      title="Sesión por expirar"
      subtitle="Tu sesión se cerrará por inactividad"
      message="No se ha detectado actividad en el sistema. Tu sesión se cerrará automáticamente en breve por seguridad."
      confirmText="Cerrar Sesión"
      cancelText="Continuar Sesión"
    />
  );
};

export default InactivityDialog;

